/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect,useState } from 'react'
import { Link,useParams } from 'react-router-dom'
import { useAuth } from '../components/Auth'
import { api,businessPath,money } from '../lib/api'
import { Badge,PageHeader,State } from '../components/UI'

type Product={
 id:string
 name:string
 product_type:string
 category_id:string|null
 description:string|null
 sku:string|null
 barcode:string|null
 purchase_price:string|null
 selling_price:string
 low_stock_alert:string|null
 expiry_date:string|null
 image_url:string|null
 track_stock:boolean
 is_active:boolean
}

type Variant={
 id:string
 name:string
 attributes:Record<string,string>
 sku:string|null
 barcode:string|null
 selling_price:string|null
 is_active:boolean
}

type Conversion={
 id:string
 unit_id:string
 quantity_in_base:string|number
 selling_price:string|null
 is_purchase_unit:boolean
 is_selling_unit:boolean
}

type Detail={
 product:Product
 category:{name:string}|null
 base_unit:{name:string;symbol:string}|null
 variants:Variant[]
 conversions:Conversion[]
}

type Category={id:string;name:string}
type Branch={id:string;name:string}
type Unit={id:string;name:string;symbol:string}

const fields=[
 'name',
 'category_id',
 'description',
 'sku',
 'barcode',
 'purchase_price',
 'selling_price',
 'low_stock_alert',
 'expiry_date',
 'image_url',
] as const

const blankVariant={
 id:'',
 name:'',
 attributes:'{}',
 sku:'',
 barcode:'',
 selling_price:'',
 is_active:true,
}

const blankConversion={
 id:'',
 unit_id:'',
 quantity_in_base:'',
 selling_price:'',
 is_purchase_unit:false,
 is_selling_unit:true,
}

export default function ProductDetailsPage(){
 const {productId}=useParams()
 const {workspace}=useAuth()

 const branches=((workspace as typeof workspace&{accessible_branches?:Branch[]}).accessible_branches
   ??(workspace.main_branch?[workspace.main_branch]:[]))

 const [detail,setDetail]=useState<Detail|null>(null)
 const [categories,setCategories]=useState<Category[]>([])
 const [units,setUnits]=useState<Unit[]>([])
 const [form,setForm]=useState<Record<string,string>>({})
 const [editing,setEditing]=useState(false)

 const [opening,setOpening]=useState(false)
 const [openingBranch,setOpeningBranch]=useState('')
 const [openingQuantity,setOpeningQuantity]=useState('')
 const [openingComment,setOpeningComment]=useState('')

 const [variantForm,setVariantForm]=useState(blankVariant)
 const [conversionForm,setConversionForm]=useState(blankConversion)

 const [loading,setLoading]=useState(true)
 const [busy,setBusy]=useState(false)
 const [error,setError]=useState('')
 const [success,setSuccess]=useState('')

 const load=async()=>{
   if(!productId)return
   setLoading(true)
   try{
     const [next,cats,unitRows]=await Promise.all([
       api<Detail>(businessPath(`/products/${productId}`)),
       api<Category[]>(businessPath('/categories')),
       api<Unit[]>(businessPath('/units')),
     ])
     setDetail(next)
     setCategories(cats)
     setUnits(unitRows)
     setForm(Object.fromEntries(fields.map(key=>[key,String(next.product[key]??'')])))
     setError('')
   }catch(e){
     setError(e instanceof Error?e.message:'Unable to load product details.')
   }finally{
     setLoading(false)
   }
 }

 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(()=>{void load()},[productId])

 const save=async()=>{
   if(!detail)return
   setBusy(true)
   setError('')
   setSuccess('')
   try{
     const body={
       ...form,
       category_id:form.category_id||null,
       description:form.description||null,
       sku:form.sku||null,
       barcode:form.barcode||null,
       purchase_price:form.purchase_price?Number(form.purchase_price):null,
       selling_price:Number(form.selling_price),
       low_stock_alert:form.low_stock_alert?Number(form.low_stock_alert):null,
       expiry_date:form.expiry_date||null,
       image_url:form.image_url||null,
     }
     await api<Product>(
       businessPath(`/products/${detail.product.id}`),
       {method:'PATCH',body:JSON.stringify(body)},
     )
     setEditing(false)
     setSuccess('Product changes saved.')
     await load()
   }catch(e){
     setError(e instanceof Error?e.message:'Unable to save product changes.')
   }finally{
     setBusy(false)
   }
 }

 const status=async()=>{
   if(!detail)return
   setBusy(true)
   setError('')
   setSuccess('')
   try{
     await api<Product>(
       businessPath(`/products/${detail.product.id}/status`),
       {
         method:'PATCH',
         body:JSON.stringify({is_active:!detail.product.is_active}),
       },
     )
     setSuccess(`Product ${detail.product.is_active?'deactivated':'activated'}.`)
     await load()
   }catch(e){
     setError(e instanceof Error?e.message:'Unable to update product status.')
   }finally{
     setBusy(false)
   }
 }

 const recordOpening=async()=>{
   if(!detail)return
   setBusy(true)
   setError('')
   setSuccess('')
   try{
     await api(
       businessPath(`/products/${detail.product.id}/opening-stock`),
       {
         method:'POST',
         body:JSON.stringify({
           branch_id:openingBranch,
           quantity:Number(openingQuantity),
           comment:openingComment||null,
         }),
       },
     )
     setSuccess('Opening stock recorded.')
     setOpening(false)
     setOpeningQuantity('')
     setOpeningComment('')
     await load()
   }catch(e){
     setError(e instanceof Error?e.message:'Unable to record opening stock.')
   }finally{
     setBusy(false)
   }
 }

 const startVariantEdit=(variant:Variant)=>{
   setVariantForm({
     id:variant.id,
     name:variant.name,
     attributes:JSON.stringify(variant.attributes,null,2),
     sku:variant.sku??'',
     barcode:variant.barcode??'',
     selling_price:variant.selling_price??'',
     is_active:variant.is_active,
   })
   setError('')
   setSuccess('')
 }

 const saveVariant=async()=>{
   if(!detail)return
   setBusy(true)
   setError('')
   setSuccess('')
   try{
     let attributes:Record<string,string>
     try{
       const parsed=JSON.parse(variantForm.attributes||'{}')
       if(!parsed || Array.isArray(parsed) || typeof parsed!=='object'){
         throw new Error()
       }
       attributes=parsed as Record<string,string>
     }catch{
       throw new Error('Variant attributes must be valid JSON, for example {"Color":"Red"}.')
     }

     const body={
       name:variantForm.name,
       attributes,
       sku:variantForm.sku||null,
       barcode:variantForm.barcode||null,
       selling_price:variantForm.selling_price?Number(variantForm.selling_price):null,
     }

     if(variantForm.id){
       await api(
         businessPath(`/products/${detail.product.id}/variants/${variantForm.id}`),
         {
           method:'PATCH',
           body:JSON.stringify({...body,is_active:variantForm.is_active}),
         },
       )
       setSuccess('Variant updated.')
     }else{
       await api(
         businessPath(`/products/${detail.product.id}/variants`),
         {method:'POST',body:JSON.stringify(body)},
       )
       setSuccess('Variant added.')
     }

     setVariantForm(blankVariant)
     await load()
   }catch(e){
     setError(e instanceof Error?e.message:'Unable to save variant.')
   }finally{
     setBusy(false)
   }
 }

 const startConversionEdit=(conversion:Conversion)=>{
   setConversionForm({
     id:conversion.id,
     unit_id:conversion.unit_id,
     quantity_in_base:String(conversion.quantity_in_base),
     selling_price:conversion.selling_price??'',
     is_purchase_unit:conversion.is_purchase_unit,
     is_selling_unit:conversion.is_selling_unit,
   })
   setError('')
   setSuccess('')
 }

 const saveConversion=async()=>{
   if(!detail)return
   if(Number(conversionForm.quantity_in_base)<=0){
     setError('Quantity in base units must be greater than zero.')
     return
   }

   setBusy(true)
   setError('')
   setSuccess('')

   try{
     if(conversionForm.id){
       await api(
         businessPath(`/products/${detail.product.id}/conversions/${conversionForm.id}`),
         {
           method:'PATCH',
           body:JSON.stringify({
             quantity_in_base:Number(conversionForm.quantity_in_base),
             selling_price:conversionForm.selling_price
               ?Number(conversionForm.selling_price)
               :null,
             is_purchase_unit:conversionForm.is_purchase_unit,
             is_selling_unit:conversionForm.is_selling_unit,
           }),
         },
       )
       setSuccess('Unit conversion updated.')
     }else{
       if(!conversionForm.unit_id){
         throw new Error('Choose a unit.')
       }
       await api(
         businessPath(`/products/${detail.product.id}/conversions`),
         {
           method:'POST',
           body:JSON.stringify({
             unit_id:conversionForm.unit_id,
             quantity_in_base:Number(conversionForm.quantity_in_base),
             selling_price:conversionForm.selling_price
               ?Number(conversionForm.selling_price)
               :null,
             is_purchase_unit:conversionForm.is_purchase_unit,
             is_selling_unit:conversionForm.is_selling_unit,
           }),
         },
       )
       setSuccess('Unit conversion added.')
     }

     setConversionForm(blankConversion)
     await load()
   }catch(e){
     setError(e instanceof Error?e.message:'Unable to save unit conversion.')
   }finally{
     setBusy(false)
   }
 }

 if(loading)return <State title="Loading product…"/>

 if(!detail){
   return <State title={error||'Product not found.'} error={Boolean(error)}/>
 }

 const p=detail.product
 const unit=detail.base_unit?.name||detail.base_unit?.symbol||'the configured base unit'
 const supportsVariants=p.product_type==='VARIATION'
 const supportsConversions=p.product_type==='PIECES_PACK'||p.product_type==='MULTI_LEVEL'

 return <section className="aad-page">
   <PageHeader
     title={p.name}
     description={`${p.product_type.replaceAll('_',' ')} product details.`}
     action={<div className="header-actions">
       <Link className="aad-button aad-button-secondary" to="/products">
         Back to products
       </Link>
       <button
         className="aad-button aad-button-secondary"
         disabled={busy}
         onClick={()=>setEditing(!editing)}
       >
         {editing?'Cancel edit':'Edit product'}
       </button>
       <button
         className="aad-button aad-button-secondary"
         disabled={busy}
         onClick={()=>{
           setOpening(!opening)
           setOpeningBranch(openingBranch||branches[0]?.id||'')
         }}
       >
         Opening stock
       </button>
       <button
         className="aad-button aad-button-gold"
         disabled={busy}
         onClick={()=>void status()}
       >
         {p.is_active?'Deactivate':'Activate'}
       </button>
     </div>}
   />

   {error&&<p className="aad-error">{error}</p>}
   {success&&<p className="aad-success">{success}</p>}

   <div className="detail-grid">
     <article className="aad-card aad-card-padding">
       <Badge value={p.is_active?'ACTIVE':'INACTIVE'}/>
       <p className="aad-muted">{p.description||'No description.'}</p>
       <p>
         Category: {detail.category?.name||'None'} · Base unit: {detail.base_unit?.symbol||'None'}
       </p>
       <p>SKU: {p.sku||'—'} · Barcode: {p.barcode||'—'}</p>
       <p>
         Purchase: {p.purchase_price===null?'—':money(p.purchase_price)}
         {' · '}
         Selling: {money(p.selling_price)}
       </p>
       <p>
         Low-stock threshold: {p.low_stock_alert??'—'} · Expiry: {p.expiry_date||'—'}
       </p>
       <p className="aad-muted">
         Product type, base unit and stock-tracking mode remain fixed after creation.
       </p>
     </article>

     <aside className="aad-card aad-card-padding">
       <h2 className="aad-section-title">Structure</h2>
       <p>{detail.variants.length} variants</p>
       <p>{detail.conversions.length} unit conversions</p>
       <p>{p.track_stock?'Stock tracked':'Stock not tracked'}</p>
     </aside>
   </div>

   {opening&&<section className="aad-card aad-card-padding">
     <h2 className="aad-section-title">Record opening stock</h2>
     <p className="aad-muted">
       Initial balance only. Quantity is in {unit}; later changes use Stock Adjustment or Restock.
     </p>
     <div className="aad-form-grid">
       <label className="aad-field">
         <span className="aad-label">Branch</span>
         <select
           className="aad-select"
           value={openingBranch}
           onChange={e=>setOpeningBranch(e.target.value)}
         >
           <option value="">Choose branch</option>
           {branches.map(branch=>
             <option key={branch.id} value={branch.id}>{branch.name}</option>
           )}
         </select>
       </label>

       <label className="aad-field">
         <span className="aad-label">Opening quantity ({unit})</span>
         <input
           className="aad-input"
           type="number"
           min=".001"
           step=".001"
           value={openingQuantity}
           onChange={e=>setOpeningQuantity(e.target.value)}
         />
       </label>

       <label className="aad-field">
         <span className="aad-label">Note (optional)</span>
         <input
           className="aad-input"
           value={openingComment}
           onChange={e=>setOpeningComment(e.target.value)}
         />
       </label>
     </div>

     <div className="form-actions">
       <button
         className="aad-button aad-button-gold"
         disabled={busy||!openingBranch||Number(openingQuantity)<=0}
         onClick={()=>void recordOpening()}
       >
         {busy?'Recording…':'Record opening stock'}
       </button>
       <button
         className="aad-button aad-button-secondary"
         disabled={busy}
         onClick={()=>setOpening(false)}
       >
         Cancel
       </button>
     </div>
   </section>}

   {editing&&<section className="aad-card aad-card-padding">
     <h2 className="aad-section-title">Edit product</h2>
     <div className="aad-form-grid">
       {fields.map(key=>
         <label className="aad-field" key={key}>
           <span className="aad-label">{key.replaceAll('_',' ')}</span>
           {key==='category_id'
             ?<select
               className="aad-select"
               value={form[key]}
               onChange={e=>setForm({...form,[key]:e.target.value})}
             >
               <option value="">No category</option>
               {categories.map(category=>
                 <option value={category.id} key={category.id}>{category.name}</option>
               )}
             </select>
             :key==='description'
               ?<textarea
                 className="aad-textarea"
                 value={form[key]}
                 onChange={e=>setForm({...form,[key]:e.target.value})}
               />
               :<input
                 className="aad-input"
                 type={key.includes('price')||key==='low_stock_alert'
                   ?'number'
                   :key==='expiry_date'?'date':'text'}
                 step=".001"
                 value={form[key]}
                 onChange={e=>setForm({...form,[key]:e.target.value})}
               />
           }
         </label>
       )}
     </div>
     <div className="form-actions">
       <button
         className="aad-button aad-button-gold"
         disabled={busy}
         onClick={()=>void save()}
       >
         {busy?'Saving…':'Save changes'}
       </button>
     </div>
   </section>}

   {supportsVariants&&<section className="aad-card aad-card-padding">
     <div className="section-heading-row">
       <div>
         <h2 className="aad-section-title">Variants</h2>
         <p className="aad-muted">
           Edit existing variants in place so inventory/history references remain intact.
         </p>
       </div>
       <button
         className="aad-button aad-button-secondary"
         disabled={busy}
         onClick={()=>setVariantForm(blankVariant)}
       >
         Add variant
       </button>
     </div>

     {detail.variants.length
       ?<div className="aad-table-wrap">
         <table className="aad-table">
           <thead>
             <tr>
               <th>Name</th>
               <th>Attributes</th>
               <th>SKU</th>
               <th>Barcode</th>
               <th>Price</th>
               <th>Status</th>
               <th>Action</th>
             </tr>
           </thead>
           <tbody>
             {detail.variants.map(variant=>
               <tr key={variant.id}>
                 <td>{variant.name}</td>
                 <td>{JSON.stringify(variant.attributes)}</td>
                 <td>{variant.sku||'—'}</td>
                 <td>{variant.barcode||'—'}</td>
                 <td>{variant.selling_price?money(variant.selling_price):'—'}</td>
                 <td><Badge value={variant.is_active?'ACTIVE':'INACTIVE'}/></td>
                 <td>
                   <button
                     className="aad-button aad-button-secondary"
                     onClick={()=>startVariantEdit(variant)}
                   >
                     Edit
                   </button>
                 </td>
               </tr>
             )}
           </tbody>
         </table>
       </div>
       :<p className="aad-muted">No variants yet.</p>
     }

     <h3>{variantForm.id?'Edit variant':'Add variant'}</h3>

     <div className="aad-form-grid">
       <label className="aad-field">
         <span className="aad-label">Name</span>
         <input
           className="aad-input"
           value={variantForm.name}
           onChange={e=>setVariantForm({...variantForm,name:e.target.value})}
         />
       </label>

       <label className="aad-field">
         <span className="aad-label">SKU</span>
         <input
           className="aad-input"
           value={variantForm.sku}
           onChange={e=>setVariantForm({...variantForm,sku:e.target.value})}
         />
       </label>

       <label className="aad-field">
         <span className="aad-label">Barcode</span>
         <input
           className="aad-input"
           value={variantForm.barcode}
           onChange={e=>setVariantForm({...variantForm,barcode:e.target.value})}
         />
       </label>

       <label className="aad-field">
         <span className="aad-label">Selling price</span>
         <input
           className="aad-input"
           type="number"
           min="0"
           step=".01"
           value={variantForm.selling_price}
           onChange={e=>setVariantForm({...variantForm,selling_price:e.target.value})}
         />
       </label>

       <label className="aad-field aad-field-full">
         <span className="aad-label">Attributes (JSON)</span>
         <textarea
           className="aad-textarea"
           value={variantForm.attributes}
           onChange={e=>setVariantForm({...variantForm,attributes:e.target.value})}
         />
       </label>

       {variantForm.id&&<label className="aad-field">
         <span className="aad-label">Active</span>
         <input
           type="checkbox"
           checked={variantForm.is_active}
           onChange={e=>setVariantForm({...variantForm,is_active:e.target.checked})}
         />
       </label>}
     </div>

     <div className="form-actions">
       <button
         className="aad-button aad-button-gold"
         disabled={busy||!variantForm.name.trim()}
         onClick={()=>void saveVariant()}
       >
         {busy?'Saving…':variantForm.id?'Save variant':'Add variant'}
       </button>
       {variantForm.id&&<button
         className="aad-button aad-button-secondary"
         disabled={busy}
         onClick={()=>setVariantForm(blankVariant)}
       >
         Cancel edit
       </button>}
     </div>
   </section>}

   {supportsConversions&&<section className="aad-card aad-card-padding">
     <div className="section-heading-row">
       <div>
         <h2 className="aad-section-title">Unit conversions</h2>
         <p className="aad-muted">
           Conversion IDs stay unchanged when edited. Inventory remains stored in base units.
         </p>
       </div>
       <button
         className="aad-button aad-button-secondary"
         disabled={busy}
         onClick={()=>setConversionForm(blankConversion)}
       >
         Add conversion
       </button>
     </div>

     {detail.conversions.length
       ?<div className="aad-table-wrap">
         <table className="aad-table">
           <thead>
             <tr>
               <th>Unit</th>
               <th>Base quantity</th>
               <th>Selling price</th>
               <th>Purchase unit</th>
               <th>Selling unit</th>
               <th>Action</th>
             </tr>
           </thead>
           <tbody>
             {detail.conversions.map(conversion=>{
               const conversionUnit=units.find(x=>x.id===conversion.unit_id)
               return <tr key={conversion.id}>
                 <td>{conversionUnit
                   ?`${conversionUnit.name} (${conversionUnit.symbol})`
                   :conversion.unit_id}
                 </td>
                 <td>{conversion.quantity_in_base}</td>
                 <td>{conversion.selling_price?money(conversion.selling_price):'—'}</td>
                 <td>{conversion.is_purchase_unit?'Yes':'No'}</td>
                 <td>{conversion.is_selling_unit?'Yes':'No'}</td>
                 <td>
                   <button
                     className="aad-button aad-button-secondary"
                     onClick={()=>startConversionEdit(conversion)}
                   >
                     Edit
                   </button>
                 </td>
               </tr>
             })}
           </tbody>
         </table>
       </div>
       :<p className="aad-muted">No unit conversions yet.</p>
     }

     <h3>{conversionForm.id?'Edit conversion':'Add conversion'}</h3>

     <div className="aad-form-grid">
       <label className="aad-field">
         <span className="aad-label">Unit</span>
         <select
           className="aad-select"
           disabled={Boolean(conversionForm.id)}
           value={conversionForm.unit_id}
           onChange={e=>setConversionForm({...conversionForm,unit_id:e.target.value})}
         >
           <option value="">Choose unit</option>
           {units.map(row=>
             <option value={row.id} key={row.id}>
               {row.name} ({row.symbol})
             </option>
           )}
         </select>
       </label>

       <label className="aad-field">
         <span className="aad-label">Quantity in base unit</span>
         <input
           className="aad-input"
           type="number"
           min=".0001"
           step=".0001"
           value={conversionForm.quantity_in_base}
           onChange={e=>setConversionForm({
             ...conversionForm,
             quantity_in_base:e.target.value,
           })}
         />
       </label>

       <label className="aad-field">
         <span className="aad-label">Selling price</span>
         <input
           className="aad-input"
           type="number"
           min="0"
           step=".01"
           value={conversionForm.selling_price}
           onChange={e=>setConversionForm({
             ...conversionForm,
             selling_price:e.target.value,
           })}
         />
       </label>

       <label className="aad-field">
         <span className="aad-label">Purchase unit</span>
         <input
           type="checkbox"
           checked={conversionForm.is_purchase_unit}
           onChange={e=>setConversionForm({
             ...conversionForm,
             is_purchase_unit:e.target.checked,
           })}
         />
       </label>

       <label className="aad-field">
         <span className="aad-label">Selling unit</span>
         <input
           type="checkbox"
           checked={conversionForm.is_selling_unit}
           onChange={e=>setConversionForm({
             ...conversionForm,
             is_selling_unit:e.target.checked,
           })}
         />
       </label>
     </div>

     <div className="form-actions">
       <button
         className="aad-button aad-button-gold"
         disabled={
           busy||
           !conversionForm.quantity_in_base||
           Number(conversionForm.quantity_in_base)<=0||
           (!conversionForm.id&&!conversionForm.unit_id)
         }
         onClick={()=>void saveConversion()}
       >
         {busy?'Saving…':conversionForm.id?'Save conversion':'Add conversion'}
       </button>
       {conversionForm.id&&<button
         className="aad-button aad-button-secondary"
         disabled={busy}
         onClick={()=>setConversionForm(blankConversion)}
       >
         Cancel edit
       </button>}
     </div>
   </section>}

   {!supportsVariants&&!supportsConversions&&<section className="aad-card aad-card-padding">
     <h2 className="aad-section-title">Product structure</h2>
     <p className="aad-muted">
       This product type does not use editable variants or additional unit conversions.
     </p>
   </section>}
 </section>
}
